import React, { ReactElement } from 'react';
import cn from '../../../utils/cn';
import { Slot } from '@radix-ui/react-slot';

export enum ButtonCTAVariant {
  PRIMARY,
  DARK_GRAY,
  GRAY,
  MIDDLE_GRAY,
  YELLOW,
  ORANGE,
  BLUE,
  VIOLET,
  RED,
  BRAND,
  LIGHT_BRAND,
  PRIMARY_OUTLINE,
  DARK_GRAY_OUTLINE,
  LIGHT_GRAY_OUTLINE,
  LIGHT_GRAY_OUTLINE_2,
  LIGHT_GRAY_LIGHTHOVER_OUTLINE,
  BRAND_OUTLINE,
  LIGHT_BRAND_OUTLINE,
  YELLOW_OUTLINE,
  RED_OUTLINE,
  ORANGE_OUTLINE,
  BLUE_OUTLINE,
  VIOLET_OUTLINE,
  DARK_GRAY_BRAND_OUTLINE,
  LINK,
  GHOST,
}

export enum ButtonCTASize {
  DEFAULT,
  SMALL,
  ICON,
}

export type ButtonCTAProps =
  | (React.ComponentPropsWithRef<'button'> & {
      asChild: true;
      variant?: ButtonCTAVariant;
      size?: ButtonCTASize;
      iconLeft?: never;
      iconRight?: never;
    })
  | (React.ComponentPropsWithRef<'button'> & {
      asChild?: false;
      variant?: ButtonCTAVariant;
      size?: ButtonCTASize;
      iconLeft?: ReactElement;
      iconRight?: ReactElement;
    });

const ButtonCTA = React.forwardRef<HTMLButtonElement, ButtonCTAProps>(function ButtonCTA(
  {
    asChild = false,
    variant = ButtonCTAVariant.PRIMARY,
    size = ButtonCTASize.DEFAULT,
    iconLeft,
    iconRight,
    disabled = false,
    className,
    children,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  const variantClassMap: Record<ButtonCTAVariant, string> = {
    [ButtonCTAVariant.PRIMARY]:
      'bg-primary text-contrast border-primary hover:shadow-primary/25 disabled:opacity-50',
    [ButtonCTAVariant.DARK_GRAY]:
      'bg-neutral-700 border-neutral-700 text-white hover:shadow-black/10 disabled:text-white disabled:bg-neutral-200 disabled:border-neutral-200',
    [ButtonCTAVariant.GRAY]:
      'bg-neutral-500 border-neutral-500 text-white hover:shadow-black/10 disabled:text-white disabled:bg-neutral-200 disabled:border-neutral-200',
    [ButtonCTAVariant.MIDDLE_GRAY]:
      'bg-neutral-300 text-neutral-500 border-neutral-300 hover:shadow-black/10 disabled:text-white disabled:bg-neutral-200 disabled:border-neutral-200',
    [ButtonCTAVariant.YELLOW]:
      'bg-yellow-500 text-white hover:shadow-yellow-500/20 border-yellow-500 disabled:opacity-50',
    [ButtonCTAVariant.ORANGE]:
      'bg-orange-500 text-white hover:shadow-orange-500/20 border-orange-500 disabled:opacity-50',
    [ButtonCTAVariant.BLUE]:
      'bg-blue-500 text-white hover:shadow-blue-500/20 border-blue-500 disabled:opacity-50',
    [ButtonCTAVariant.VIOLET]:
      'bg-violet-500 text-white hover:shadow-violet-500/20 border-violet-500 disabled:opacity-50',
    [ButtonCTAVariant.RED]:
      'bg-red-500 text-white hover:shadow-red-500/20 border-red-500 disabled:opacity-50',
    [ButtonCTAVariant.BRAND]:
      'bg-brand text-neutral-700 hover:shadow-brand/25 disabled:opacity-50 border-brand',
    [ButtonCTAVariant.LIGHT_BRAND]:
      'bg-brand text-white hover:shadow-brand/20 border-brand disabled:opacity-50',
    [ButtonCTAVariant.PRIMARY_OUTLINE]:
      'bg-white text-neutral-700 border-primary hover:shadow-primary/25 disabled:text-neutral-300 disabled:bg-neutral-100 disabled:border-neutral-200',
    [ButtonCTAVariant.DARK_GRAY_OUTLINE]:
      'bg-white text-neutral-700 border-neutral-700 hover:shadow-black/10 disabled:opacity-50',
    [ButtonCTAVariant.LIGHT_GRAY_OUTLINE]:
      'bg-white text-neutral-700 border-neutral-200 hover:shadow-black/10 disabled:text-neutral-300 disabled:bg-neutral-100',
    [ButtonCTAVariant.LIGHT_GRAY_OUTLINE_2]:
      'bg-white text-neutral-500 border-neutral-200 hover:shadow-black/10 disabled:text-neutral-300 disabled:bg-neutral-100',
    [ButtonCTAVariant.LIGHT_GRAY_LIGHTHOVER_OUTLINE]:
      'bg-white text-neutral-500 border-neutral-200 hover:shadow-none hover:border-neutral-400 disabled:bg-white disabled:text-neutral-300',
    [ButtonCTAVariant.BRAND_OUTLINE]:
      'bg-white text-brand border-brand hover:border-brand-600 font-bold hover:shadow',
    [ButtonCTAVariant.LIGHT_BRAND_OUTLINE]:
      'bg-white text-brand hover:bg-brand-50 hover:shadow-none border-brand',
    [ButtonCTAVariant.YELLOW_OUTLINE]:
      'bg-white text-yellow-500 hover:bg-yellow-50 hover:shadow-none border-yellow-500',
    [ButtonCTAVariant.RED_OUTLINE]:
      'bg-white text-red-500 hover:bg-red-50 hover:shadow-none border-red-500',
    [ButtonCTAVariant.ORANGE_OUTLINE]:
      'bg-white text-orange-500 hover:bg-orange-50 hover:shadow-none border-orange-500',
    [ButtonCTAVariant.BLUE_OUTLINE]:
      'bg-white text-blue-500 hover:bg-blue-50 hover:shadow-none border-blue-500',
    [ButtonCTAVariant.VIOLET_OUTLINE]:
      'bg-white text-violet-500 hover:bg-violet-50 hover:shadow-none border-violet-500',
    [ButtonCTAVariant.DARK_GRAY_BRAND_OUTLINE]:
      'bg-white text-neutral-700 border-neutral-700 hover:shadow-black/10 disabled:text-neutral-300 disabled:border-neutral-700 disabled:opacity-50',
    [ButtonCTAVariant.LINK]:
      'border-0 bg-transparent p-0 font-semibold text-white antialiased hover:underline hover:shadow-none',
    [ButtonCTAVariant.GHOST]:
      'border-0 bg-transparent p-0 font-semibold text-white antialiased hover:underline hover:shadow-none',
  };

  const sizeClassMap: Record<ButtonCTASize, string> = {
    [ButtonCTASize.DEFAULT]: 'py-[9px] px-[23px] text-sm [&>svg]:size-3',
    [ButtonCTASize.SMALL]: 'py-[5px] px-[15px] text-xs [&>svg]:size-2.5',
    [ButtonCTASize.ICON]: 'p-2 size-12 [&>svg]:size-4 [&>svg]:fill-neutral-500',
  };

  const variantClassName = variantClassMap[variant];
  const sizeClassName = sizeClassMap[size];

  return (
    <Comp
      ref={ref}
      className={cn(
        'relative flex cursor-pointer flex-row items-center justify-center gap-1.5 rounded-full border border-solid font-semibold transition hover:shadow-md focus:outline-none disabled:pointer-events-none disabled:cursor-default',
        variantClassName,
        sizeClassName,
        className,
      )}
      disabled={disabled}
      {...rest}
    >
      {/* Todo : ButtonCTA needs to be rewritten to use the Compound pattern and perfectly handle Icons */}
      {!asChild ? (
        <>
          {iconLeft &&
            React.cloneElement(iconLeft, {
              className: cn(
                [
                  ButtonCTAVariant.DARK_GRAY_OUTLINE,
                  ButtonCTAVariant.LIGHT_GRAY_OUTLINE,
                  ButtonCTAVariant.LINK,
                ].includes(variant) &&
                  `${disabled ? 'text-neutral-300' : 'text-primary'}`,
                iconLeft.props.className,
              ),
            })}
          {children}
          {iconRight &&
            React.cloneElement(iconRight, {
              className: cn(
                [
                  ButtonCTAVariant.DARK_GRAY_OUTLINE,
                  ButtonCTAVariant.LIGHT_GRAY_OUTLINE,
                  ButtonCTAVariant.LINK,
                ].includes(variant) &&
                  `${disabled ? 'text-neutral-300' : 'text-primary'}`,
                iconRight.props.className,
              ),
            })}
        </>
      ) : (
        children
      )}
    </Comp>
  );
});

export default ButtonCTA;
